import React from "react";
import ContentButton from "../core/ui/ContentButton";

const HomepageHero: React.FC = () => {
  return (
    <section className="cs-hero" data-testid="home-hero">
      <div className="cs-hero-inner">
        <h1>
          Operational software<br />
          <span className="dim">for your business.</span><br />
          Built on a data layer that <span className="accent">defends itself.</span>
        </h1>

        <p className="cs-hero-lede">
          Data FabrIQ replaces the spreadsheets, scripts, and Slack threads your team uses to run{" "}
          <strong>pipeline reviews, MRR builds, commission runs, and variance reports</strong> —
          with purpose-built workflows on top of a unified data model where every number traces
          back to its source.
        </p>

        <div className="cs-hero-actions">
          <ContentButton
            href="https://calendar.app.google/rQuDdWM57HswfdSB9"
            variant="primary"
            size="lg"
            target="_blank"
            rel="noopener noreferrer"
          >
            Request access →
          </ContentButton>
          <ContentButton href="#how-it-works" variant="secondary" size="lg">
            See how it works
          </ContentButton>
        </div>

        <div className="cs-hero-visual">
          <div className="hv-chrome">
            <div className="dots"><span /><span /><span /></div>
            <div className="url">
              <span className="dim">app.datafabriq.co/</span>
              <span className="accent">acme-saas</span>
              <span className="dim">/dashboards/mrr-rollforward</span>
            </div>
          </div>

          <div className="hv-dashboard">
            <div className="hv-app-header">
              <h3>Dashboard – MRR Rollforward</h3>
              <div className="hv-app-header-right">
                <span className="picker">⊞ MRR Rollforward ▾</span>
                <span className="share">⇧ Share</span>
              </div>
            </div>

            <div className="hv-drill-layout">
              <div className="hv-table-card">
                <div className="hv-table-title">Monthly MRR Rollforward</div>
                <div className="hv-table-sub">
                  Revenue movement by customer <span className="pipe">|</span> Excludes Acme,
                  Lattice Labs <span className="pipe">|</span> Volatile usage customers flagged
                </div>

                <div className="hv-tabs">
                  <span className="hv-tab-i">Totals</span>
                  <span className="hv-tab-i">Total MRR</span>
                  <span className="hv-tab-i">New Logo</span>
                  <span className="hv-tab-i active">Cross-sell</span>
                  <span className="hv-tab-i">Price/Upsell</span>
                  <span className="hv-tab-i">Downgrade</span>
                  <span className="hv-tab-i">Churn</span>
                  <span className="hv-tab-i">Exceptions</span>
                </div>

                <div className="hv-hint">
                  Click any value to see the audit trail and propose an override.
                </div>

                <table className="hv-rf-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Jan '25</th>
                      <th>Feb '25</th>
                      <th>Mar '25</th>
                      <th>Apr '25</th>
                      <th>May '25</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Aperture Solutions</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Acme Inc.</td><td className="val-blue">1,212</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Banyan Software</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Beam Living</td><td>0</td><td>0</td><td className="val-blue">1,269</td><td>0</td><td>0</td></tr>
                    <tr><td>Cascade Systems</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Cedar Ridge</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr className="highlighted"><td>Cirrus Labs</td><td>0</td><td>0</td><td>0</td><td>0</td><td className="val-blue">2,104</td></tr>
                    <tr><td>Delta Cloud</td><td className="val-blue">360</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Echo Industries</td><td className="val-blue">1,206</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                    <tr><td>Fathom Data</td><td>0</td><td>0</td><td>0</td><td>0</td><td className="val-blue">433</td></tr>
                    <tr><td>Glacier Labs</td><td className="val-blue">435</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="hv-drill-card">
                <div className="hv-drill-head">
                  <span className="lb">Entry Audit — May 2025</span>
                  <span className="cl">×</span>
                </div>

                <div>
                  <div className="hv-drill-customer">Cirrus Labs</div>
                  <div className="hv-drill-tags">
                    <span className="hv-drill-tag cross-sell">Cross-sell</span>
                    <span className="hv-drill-tag medium">Medium</span>
                  </div>
                </div>

                <div className="hv-drill-stats">
                  <div className="hv-drill-stat">
                    <div className="k">Beginning</div>
                    <div className="v">$2,965</div>
                  </div>
                  <div className="hv-drill-stat pos">
                    <div className="k">Ending</div>
                    <div className="v">$5,069</div>
                  </div>
                  <div className="hv-drill-stat pos">
                    <div className="k">Impact</div>
                    <div className="v">$2,105</div>
                  </div>
                </div>

                <div>
                  <div className="hv-logic-head" style={{ marginBottom: 10 }}>Classification Logic</div>
                  <div className="hv-logic">
                    <div className="hv-logic-step">
                      <div className="dot" />
                      <div className="line">
                        <div className="main">Ending $5,069 &gt; Beginning $2,965</div>
                        <div className="sub">MRR increased</div>
                      </div>
                      <span className="badge">PASS</span>
                    </div>
                    <div className="hv-logic-step">
                      <div className="dot" />
                      <div className="line">
                        <div className="main">Increase = $2,104 (71.0%)</div>
                        <div className="sub">&gt;$50 AND &gt;5% threshold check</div>
                      </div>
                      <span className="badge">PASS</span>
                    </div>
                    <div className="hv-logic-step">
                      <div className="dot" />
                      <div className="line">
                        <div className="main">Matched to Salesforce opportunity</div>
                        <div className="sub">SF opp_40219 · Renewal / Cross-sell</div>
                      </div>
                      <span className="badge">PASS</span>
                    </div>
                    <div className="hv-logic-step result">
                      <div className="dot" />
                      <div className="line">
                        <div className="main">Cross-sell value = $2,104</div>
                        <div className="sub">Final valuation</div>
                      </div>
                      <span className="badge">RESULT</span>
                    </div>
                  </div>
                </div>

                <div className="hv-override">
                  <div>
                    <div className="t">Override this entry</div>
                    <div className="s">Generates a directive for chat.</div>
                  </div>
                  <button className="hv-override-btn">Start</button>
                </div>

                <div>
                  <div className="hv-sf-head" style={{ marginBottom: 8 }}>Salesforce Context</div>
                  <div className="hv-sf">
                    <div className="hv-sf-cell"><span className="k">Record Type</span><span className="v">Renewal</span></div>
                    <div className="hv-sf-cell"><span className="k">Deal Type</span><span className="v">Renewal</span></div>
                    <div className="hv-sf-cell"><span className="k">Platform</span><span className="v">Platform</span></div>
                    <div className="hv-sf-cell"><span className="k">Opportunity</span><span className="v link">View in SF →</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomepageHero;
